import React, { useState, useEffect } from "react";
import blackStar from "../assets/images/icons/blackstar.png";
import filledStar from "../assets/images/navbar/orange-star.png";

interface RatingModalProps {
  onClose: () => void;
  orderId: string;
  type: "restaurant" | "delivery-agent";
  restaurantId?: string;
  restaurantName?: string;
  deliveryAgentId?: string;
  deliveryAgentName?: string;
  initialRating?: number;
  onSubmit: (rating: number, review: string) => Promise<void>;
}

const RatingModal = ({
  onClose,
  orderId,
  type,
  restaurantId,
  restaurantName,
  deliveryAgentId,
  deliveryAgentName = "Delivery Partner",
  initialRating = 0,
  onSubmit,
}: RatingModalProps) => {
  const title = type === "restaurant" ? "Rate Restaurant" : "Rate Delivery Partner";
  const subtitle = type === "restaurant" ? restaurantName : deliveryAgentName;
  const placeholderText = type === "restaurant" 
    ? "Share your experience about the food and service..."
    : "Share your experience about the delivery service...";
  
  const [rating, setRating] = useState<number>(initialRating);
  const [review, setReview] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    setRating(initialRating);
  }, [initialRating]);

  const handleSubmit = async () => {
    if (rating === 0) {
      setError("Please select a rating");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await onSubmit(rating, review);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to submit rating");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Backdrop Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-[1005] transition-colors duration-300"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="fixed inset-0 flex items-center justify-center z-[1006] pointer-events-none">
        <div
          className="w-[90%] max-w-[480px] p-6 md:p-8 rounded-[24px] bg-white dark:bg-gray-800 pointer-events-auto relative transition-colors duration-300"
          style={{
            boxShadow: "-23px 23px 72px 0 rgba(0, 0, 0, 0.15)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-[#050505] dark:text-white font-urbanist text-[24px] font-semibold leading-[140%] mb-2">
              {title}
            </h2>
            <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[14px] font-normal leading-[160%]">
              {subtitle}
            </p>
          </div>

          {/* Star Rating */}
          <div className="flex justify-center items-center gap-3 mb-6">
            {Array(5)
              .fill(0)
              .map((_, index) => (
                <img
                  key={index}
                  src={index < rating ? filledStar : blackStar}
                  alt={index < rating ? "filled star" : "empty star"}
                  className="h-[40px] w-[40px] cursor-pointer transition-transform hover:scale-110"
                  onClick={() => setRating(index + 1)}
                />
              ))}
          </div>

          {/* Selected Rating Display */}
          {rating > 0 && (
            <div className="text-center mb-4">
              <p className="text-[#7604A5] dark:text-purple-400 font-urbanist text-[16px] font-medium">
                {rating} {rating === 1 ? "star" : "stars"} selected
              </p>
            </div>
          )}

          {/* Review Text Area */}
          <div className="mb-6">
            <label
              htmlFor="review"
              className="block text-[#050505] dark:text-white font-urbanist text-[14px] font-medium mb-2"
            >
              Write a Review
            </label>
            <textarea
              id="review"
              value={review}
              onChange={(e) => setReview(e.target.value)}
              placeholder={placeholderText}
              className="w-full h-[120px] p-4 border-2 border-gray-200 dark:border-gray-600 rounded-[12px] bg-white dark:bg-gray-700 text-[#050505] dark:text-white font-urbanist text-[14px] resize-none focus:outline-none focus:border-[#7604A5] dark:focus:border-purple-400 transition-colors duration-300"
              maxLength={500}
            />
            <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[12px] mt-1 text-right">
              {review.length}/500
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-[8px]">
              <p className="text-red-600 dark:text-red-400 font-urbanist text-[14px]">
                {error}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 py-3 px-6 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-[#050505] dark:text-white font-urbanist text-[16px] font-semibold rounded-[150px] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || rating === 0}
              className="flex-1 py-3 px-6 bg-[#7604A5] hover:bg-[#5a0383] dark:bg-purple-600 dark:hover:bg-purple-700 text-white font-urbanist text-[16px] font-semibold rounded-[150px] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Submitting..." : "Submit Rating"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RatingModal;
